// src/pages/orders/components/bulk/LeadUploadOptions.jsx
import React, { useState, useCallback, useRef, useEffect } from 'react';
import { Search, X, Package, MapPin, Clock, AlertTriangle } from 'lucide-react';
import { searchProducts } from '../../../../services/api/productSearch';
import styles from './LeadUploadOptions.module.css';

const DUPLICATE_DAY_OPTIONS = [0, 1, 2, 3, 4, 5, 6, 7];

const LeadUploadOptions = ({
  shopId,
  defaultProduct,
  onDefaultProductChange,
  useCityUnknown,
  onUseCityUnknownChange,
  duplicateCheckDays,
  onDuplicateCheckDaysChange,
  disabled,
  isResellerDropshipper = false,
}) => {
  const [searchTerm,    setSearchTerm]    = useState('');
  const [searchResults, setResults]       = useState([]);
  const [isSearching,   setIsSearching]   = useState(false);
  const [showDropdown,  setShowDropdown]  = useState(false);
  const [hasSearched,   setHasSearched]   = useState(false);
  const timeoutRef  = useRef(null);
  const wrapperRef  = useRef(null);
  const searchIdRef = useRef(0);

  useEffect(() => {
    const handler = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setShowDropdown(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const doSearch = useCallback(async (term) => {
    if (!term.trim() || !shopId) { setResults([]); setShowDropdown(false); return; }
    const id = ++searchIdRef.current;
    setIsSearching(true);
    try {
      const res = await searchProducts(shopId, term.trim(), { limit: 8, page: 1 });
      if (id !== searchIdRef.current) return;
      setResults(res.products || []);
      setShowDropdown(true);
      setHasSearched(true);
    } catch {
      if (id === searchIdRef.current) setResults([]);
    } finally {
      if (id === searchIdRef.current) setIsSearching(false);
    }
  }, [shopId]);

  const handleSearchInput = (e) => {
    const value = e.target.value;
    setSearchTerm(value);
    clearTimeout(timeoutRef.current);
    if (!value.trim()) { setResults([]); setShowDropdown(false); return; }
    timeoutRef.current = setTimeout(() => doSearch(value), 300);
  };

  const handleSelectProduct = (product) => {
    // Cancel any pending debounce and invalidate any in-flight search
    clearTimeout(timeoutRef.current);
    searchIdRef.current++;
    // Allow out-of-stock products — no guard here
    onDefaultProductChange({
      id:          product.id,
      variationId: product.variationId || null,
      sku:         product.sku,
      name:        product.displayName,
      quantity:    1,
    });
    setSearchTerm('');
    setResults([]);
    setShowDropdown(false);
    setHasSearched(false);
    if (!useCityUnknown) onUseCityUnknownChange(true);
  };

  const handleClearProduct = () => onDefaultProductChange(null);

  const handleQtyChange = (e) => {
    const qty = Math.max(1, parseInt(e.target.value) || 1);
    if (defaultProduct) onDefaultProductChange({ ...defaultProduct, quantity: qty });
  };

  return (
    <div className={`${styles.panel} ${disabled ? styles.panelDisabled : ''}`}>

      {/* ── Header row ─────────────────────────────────────────────────────── */}
      <div className={styles.panelHeader}>
        <div className={styles.sectionHeader}>
          <Package size={18} />
          <h3>Lead Upload Options</h3>
          <span className={styles.optionalBadge}>Optional</span>
        </div>
      </div>

      {/* ── 3-column options row ────────────────────────────────────────────── */}
      <div className={styles.optionsRow}>

        {/* Default Product */}
        <div className={styles.optionBlock}>
          <label className={styles.fieldLabel}>
            <Package size={13} />
            Default Product
          </label>
          <p className={styles.fieldHint}>Applied when a row has no valid SKU.</p>

          {defaultProduct && (
            <div className={styles.selectedProduct}>
              <div className={styles.selectedProductInfo}>
                <span className={styles.selectedProductName}>{defaultProduct.name}</span>
                <span className={styles.selectedProductSku}>{defaultProduct.sku}</span>
              </div>
              <div className={styles.selectedProductQty}>
                <label>Qty</label>
                <input
                  type="number"
                  value={defaultProduct.quantity}
                  onChange={handleQtyChange}
                  min="1"
                  className={styles.qtyInput}
                  disabled={disabled}
                />
              </div>
              {!disabled && (
                <button type="button" onClick={handleClearProduct} className={styles.clearProductBtn} title="Remove">
                  <X size={13} />
                </button>
              )}
            </div>
          )}
          <div
            className={styles.productSearchWrapper}
            ref={wrapperRef}
            style={defaultProduct ? { display: 'none' } : undefined}
          >
            <div className={styles.searchInputRow}>
              <Search size={14} className={styles.searchIcon} />
              <input
                type="text"
                value={searchTerm}
                onChange={handleSearchInput}
                onFocus={() => { if (hasSearched && searchResults.length > 0) setShowDropdown(true); }}
                placeholder={shopId ? 'Search by name or SKU…' : 'Select a shop first'}
                className={styles.searchInput}
                disabled={disabled || !shopId}
              />
              {isSearching && <span className={styles.searchSpinner} />}
            </div>

            {showDropdown && (
              <div className={styles.dropdown}>
                {searchResults.length === 0 ? (
                  <div className={styles.noResults}>No products found</div>
                ) : (
                  searchResults.map(product => {
                    const stockHidden = product.stockQuantity === null;
                    const outOfStock  = !stockHidden && product.stockQuantity <= 0;
                    const displayName = product.displayName;
                    return (
                      <button
                        key={`${product.id}-${product.variationId || 'simple'}`}
                        type="button"
                        className={styles.dropdownItem}
                        onClick={() => handleSelectProduct(product)}
                      >
                        <div className={styles.productRow}>
                          <span className={styles.productName}>{displayName}</span>
                          <span className={styles.productSku}>{product.sku}</span>
                        </div>
                        <div className={styles.productMeta}>
                          <span className={styles.productPrice}>Rs. {product.price?.toFixed(2)}</span>
                          {!stockHidden && (
                            <span className={`${styles.stockBadge} ${outOfStock ? styles.stockOut : styles.stockIn}`}>
                              {outOfStock ? 'Out of stock' : `${product.stockQuantity} in stock`}
                            </span>
                          )}
                        </div>
                      </button>
                    );
                  })
                )}
              </div>
            )}
          </div>
        </div>

        {/* City Unknown */}
        <div className={styles.optionBlock}>
          <label className={styles.fieldLabel}>
            <MapPin size={13} />
            City Fallback
          </label>
          <p className={styles.fieldHint}>Rows with empty or unrecognised city get "CITY UNKNOWN".</p>
          <label className={styles.checkboxLabel}>
            <input
              type="checkbox"
              checked={useCityUnknown}
              onChange={e => onUseCityUnknownChange(e.target.checked)}
              disabled={disabled}
              className={styles.checkbox}
            />
            Use <strong>CITY UNKNOWN</strong> for unrecognised cities
          </label>
        </div>

        {/* Duplicate Detection */}
        <div className={styles.optionBlock}>
          <label className={styles.fieldLabel}>
            <Clock size={13} />
            Duplicate Detection
          </label>
          <p className={styles.fieldHint}>Flag rows matching an existing order (same phone + product) within this window.</p>
          <div className={styles.daySelector}>
            {DUPLICATE_DAY_OPTIONS.map(day => (
              <button
                key={day}
                type="button"
                className={`${styles.dayBtn} ${duplicateCheckDays === day ? styles.dayBtnActive : ''}`}
                onClick={() => onDuplicateCheckDaysChange(day)}
                disabled={disabled}
              >
                {day}d
              </button>
            ))}
          </div>
          {duplicateCheckDays > 0 && (
            <p className={styles.dupNote}>
              <AlertTriangle size={11} />
              Last <strong>{duplicateCheckDays} day{duplicateCheckDays > 1 ? 's' : ''}</strong>
            </p>
          )}
        </div>

      </div>
    </div>
  );
};

export default LeadUploadOptions;